/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseApi from '../../api/BaseApi';
import {Events} from '@exc/shared';
import {FRESH_MS} from '@adobe/exc-app/network/DataPrefetchContract';
import type {PrefetchOptions, PrefetchResponse} from '@adobe/exc-app/network';

export class NetworkPrefetchApi extends BaseApi {
  protected keyPrefix = 'network.prefetch';
  protected apiEvents = ['network.prefetch'];
  protected logPrefix = 'NetworkPrefetch';
  protected event = Events.DATA_PREFETCH;
  public prefetch = async <T>(key: string, options?: PrefetchOptions) => {
    const now = Date.now();
    const response = await this.sendAndReceive<PrefetchResponse<T>>(Events.DATA_PREFETCH, {key, method: 'GET', options});
    const {createdAt} = response;
    if (createdAt + FRESH_MS < now) {
      // If the data is older than FRESH_MS (60s), provide a convenience function "getFresh".
      // getFresh will call the API again and request newer data.
      // This allows an application present older data and later synchronize to the latest data
      response.getFresh = () => this.prefetch<T>(key, {...(options || {}), createdAfter: createdAt});
    }
    return response;
  };
}
